import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(slug || '', 3);

  const pageTitle = `${article.title} - ${siteInfo.name}`;
  const pageUrl = `${siteInfo.url}/article/${article.slug}`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    url: pageUrl,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
        { '@type': 'ListItem', position: 2, name: article.category, item: `${siteInfo.url}/category/${article.categorySlug}` },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={pageUrl}
        ogType="article"
        ogImage={article.coverImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <article className="pt-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="mb-8">
            <Link
              to={`/category/${article.categorySlug}`}
              className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors mb-4"
            >
              {article.category}
            </Link>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          <div
            className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:font-heading [&_h3]:text-xl [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul_li]:mb-2
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol_li]:mb-2
              [&_strong]:text-foreground-900 [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-10">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200/70"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="border-t border-background-200/70 pt-8 mb-10">
            <div className="flex items-center gap-4 p-5 bg-background-100 rounded-lg">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-2xl text-primary-500"></i>
              </div>
              <div>
                <p className="font-heading text-foreground-950 font-bold">{article.author}</p>
                <p className="text-sm text-foreground-500">同人动画创作者</p>
              </div>
            </div>
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        <div className="pb-14"></div>
      </article>
    </>
  );
}